import { createContext, useContext, useEffect, useState } from "react";
import { getSkin, applySkin, getLayout, canUseSkin, DEFAULT_SKIN } from "../lib/theme";
import { useAuth } from "./AuthContext";
import api from "../lib/api";
import { trackEvent } from "../lib/track";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user, patchUser } = useAuth();
  const [skin, setSkinState] = useState(getSkin());

  // apply skin to <html> whenever it changes
  useEffect(() => {
    applySkin(skin);
  }, [skin]);

  // sync FROM the account when the user loads (persists across devices)
  useEffect(() => {
    if (!user) return;
    const isAdmin = !!user.is_admin;
    // Fallback safety: if the saved skin isn't released yet (and not admin), revert.
    if (user.skin && !canUseSkin(user.skin, isAdmin)) {
      const applied = applySkin(DEFAULT_SKIN);
      setSkinState(applied);
      patchUser({ skin: applied });
      api.put("/user/skin", { skin: applied }).catch(() => {});
      return;
    }
    if (user.skin && user.skin !== skin) {
      setSkinState(applySkin(user.skin));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.skin]);

  const setSkin = (id) => {
    const applied = applySkin(id);
    setSkinState(applied);
    trackEvent("skin_change", { skin: applied });
    if (user) {
      patchUser({ skin: applied });
      api.put("/user/skin", { skin: applied }).catch(() => {});
    }
  };

  const layout = getLayout(skin);

  return (
    <ThemeContext.Provider value={{ skin, setSkin, layout, isEarly: skin === "early2010s", isLight: layout.light }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
